'use client';

import React, { useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import Header from '@/components/Header';
import { Footer } from '@/components/Footer';
import {
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiMapPin,
  FiUser,
  FiPhone,
  FiBook,
  FiTarget,
  FiCreditCard,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useRouter } from 'next/navigation';

declare global {
  interface Window {
    Razorpay: any;
  }
}

const INTERNSHIP_PRICE_PAISE = 500000;
const INTERNSHIP_PRICE_RUPEES = INTERNSHIP_PRICE_PAISE / 100;

const STEP_TITLES = ['Basic Details', 'Academic Details', 'Payment'];

export default function PaidInternshipsPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [details, setDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    collegeName: '',
    course: '',
    academicYear: '',
    city: '',
    motivation: '',
  });

  const programDetails = useMemo(
    () => [
      { field: 'Duration', value: '2 Months' },
      { field: 'Mode', value: 'Hybrid' },
      { field: 'Daily Hours', value: '4 Hours' },
      { field: 'Start Date', value: '1 May 2026' },
      { field: 'End Date', value: '30 June 2026' },
      { field: 'Certification', value: 'Yes' },
    ],
    []
  );

  const preloadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true));
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Razorpay')));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  const openEnrollModal = () => {
    if (status === 'unauthenticated') {
      toast.error('Please login first to enroll in internship');
      router.push('/login');
      return;
    }

    setDetails((prev) => ({
      ...prev,
      fullName: prev.fullName || session?.user?.name || '',
      email: prev.email || session?.user?.email || '',
    }));

    setStep(1);
    setShowModal(true);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!details.fullName.trim() || !details.email.trim() || !details.phone.trim()) {
        toast.error('Please fill name, email and phone');
        return false;
      }
    }

    if (step === 2) {
      if (!details.collegeName.trim() || !details.course.trim() || !details.academicYear.trim() || !details.city.trim()) {
        toast.error('Please fill all academic details');
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((prev) => Math.min(3, prev + 1));
  };

  const handlePrev = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);

      await preloadRazorpayScript();

      const createOrderRes = await fetch('/api/internship/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: INTERNSHIP_PRICE_PAISE,
          internshipTitle: 'AI + Software Testing & ML Internship',
          details,
          startDate: '2026-05-01T00:00:00.000Z',
          endDate: '2026-06-30T23:59:59.999Z',
        }),
      });

      const createOrderData = await createOrderRes.json();
      if (!createOrderRes.ok) {
        throw new Error(createOrderData?.error || 'Failed to create internship order');
      }

      const options = {
        key: createOrderData.keyId,
        amount: createOrderData.amount,
        currency: createOrderData.currency,
        order_id: createOrderData.orderId,
        name: 'NITMiner Technologies',
        description: 'Paid Internship Enrollment',
        prefill: {
          name: details.fullName,
          email: details.email,
          contact: details.phone,
        },
        theme: {
          color: '#2563eb',
        },
        handler: async (response: any) => {
          try {
            const verifyRes = await fetch('/api/internship/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                enrollmentId: createOrderData.enrollmentId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            const verifyData = await verifyRes.json();
            if (!verifyRes.ok) {
              throw new Error(verifyData?.error || 'Payment verification failed');
            }

            toast.success('Enrollment successful. Receipt has been sent to your email.');
            setShowModal(false);
            router.push('/internship-success');
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Payment verification failed');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setProcessing(false);
      toast.error(error instanceof Error ? error.message : 'Unable to start payment');
    }
  };

  return (
    <>

      <main className="min-h-screen bg-linear-to-b from-slate-50 via-white to-slate-100 dark:from-black dark:via-zinc-950 dark:to-zinc-900">
        <section className="px-4 sm:px-6 lg:px-12 py-14 sm:py-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide">
                Paid Internship 2026
              </span>
              <h1 className="mt-4 text-4xl sm:text-5xl font-black text-gray-900 dark:text-white leading-tight">
                Build Real Skills in AI, Testing, and ML
              </h1>
              <p className="mt-5 text-base sm:text-lg text-gray-700 dark:text-gray-300 max-w-xl">
                Learn with project-based mentorship in a 2-month hybrid internship program by NITMiner Technologies.
                Get certification, practical exposure, and a structured learning roadmap.
              </p>

              <div className="mt-6 flex items-center gap-3">
                <span className="text-2xl sm:text-3xl line-through text-gray-500">₹10,000</span>
                <span className="text-4xl sm:text-5xl font-black text-green-600">₹{INTERNSHIP_PRICE_RUPEES.toLocaleString('en-IN')}</span>
                <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">50% OFF</span>
              </div>

              <button
                onClick={openEnrollModal}
                className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-lg shadow-xl transition"
              >
                Enrol Now
                <FiArrowRight className="w-5 h-5" />
              </button>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Secure payment powered by Razorpay</p>
            </div>

            <div className="rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 shadow-xl p-6 sm:p-8 backdrop-blur">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-5">Program Snapshot</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {programDetails.map((detail) => (
                  <div key={detail.field} className="rounded-xl border border-gray-200 dark:border-zinc-700 p-4 bg-white dark:bg-zinc-900">
                    <p className="text-xs uppercase tracking-wider font-bold text-gray-500">{detail.field}</p>
                    <p className="mt-2 text-sm font-black text-gray-900 dark:text-gray-100">{detail.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2"><FiCalendar /> Starts 1 May 2026</div>
                <div className="flex items-center gap-2"><FiClock /> 4 hours/day</div>
                <div className="flex items-center gap-2"><FiMapPin /> Hybrid mode</div>
              </div>
            </div>
          </div>
        </section>

        {showModal && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 shadow-2xl overflow-hidden">
              <div className="px-5 sm:px-7 py-5 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">Internship Enrollment</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Step {step} of 3 - {STEP_TITLES[step - 1]}</p>
                </div>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900 dark:hover:text-white">
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <div className="px-5 sm:px-7 py-4">
                <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-zinc-700 overflow-hidden">
                  <div className="h-2 bg-linear-to-r from-blue-600 to-indigo-600 transition-all" style={{ width: `${(step / 3) * 100}%` }} />
                </div>
              </div>

              <div className="px-5 sm:px-7 pb-7 space-y-4">
                {step === 1 && (
                  <>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-2"><FiUser /> Full Name</label>
                      <input
                        value={details.fullName}
                        onChange={(e) => setDetails((prev) => ({ ...prev, fullName: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-2"><FiUser /> Email</label>
                      <input
                        type="email"
                        value={details.email}
                        onChange={(e) => setDetails((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-2"><FiPhone /> Phone</label>
                      <input
                        value={details.phone}
                        onChange={(e) => setDetails((prev) => ({ ...prev, phone: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-2"><FiBook /> College Name</label>
                      <input
                        value={details.collegeName}
                        onChange={(e) => setDetails((prev) => ({ ...prev, collegeName: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2 block">Course</label>
                        <input
                          value={details.course}
                          onChange={(e) => setDetails((prev) => ({ ...prev, course: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2 block">Academic Year</label>
                        <input
                          value={details.academicYear}
                          onChange={(e) => setDetails((prev) => ({ ...prev, academicYear: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2 block">City</label>
                      <input
                        value={details.city}
                        onChange={(e) => setDetails((prev) => ({ ...prev, city: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-2"><FiTarget /> Why do you want to join?</label>
                      <textarea
                        value={details.motivation}
                        onChange={(e) => setDetails((prev) => ({ ...prev, motivation: e.target.value }))}
                        rows={3}
                        className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-3 bg-white dark:bg-zinc-800 resize-none"
                        placeholder="Your career goals or expectations"
                      />
                    </div>
                  </>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 dark:border-zinc-700 p-4 bg-gray-50 dark:bg-zinc-800">
                      <h4 className="font-black text-gray-900 dark:text-white mb-3">Review & Pay</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-semibold">Name:</span> {details.fullName}</p>
                        <p><span className="font-semibold">Email:</span> {details.email}</p>
                        <p><span className="font-semibold">Phone:</span> {details.phone}</p>
                        <p><span className="font-semibold">College:</span> {details.collegeName}</p>
                        <p><span className="font-semibold">Course:</span> {details.course} ({details.academicYear})</p>
                        <p><span className="font-semibold">City:</span> {details.city}</p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-4">
                      <p className="text-xs uppercase tracking-wide font-bold text-blue-700 dark:text-blue-300">Final Payment</p>
                      <p className="text-3xl font-black text-blue-700 dark:text-blue-300 mt-1">₹{INTERNSHIP_PRICE_RUPEES.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-blue-700/80 dark:text-blue-300/80 mt-1">Step 3 opens Razorpay checkout for secure payment.</p>
                    </div>
                  </div>
                )}

                <div className="pt-2 flex items-center justify-between gap-3">
                  <button
                    onClick={step === 1 ? () => setShowModal(false) : handlePrev}
                    className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-zinc-600 font-bold"
                  >
                    {step === 1 ? 'Cancel' : 'Back'}
                  </button>

                  {step < 3 ? (
                    <button onClick={handleNext} className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold">
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handlePayment}
                      disabled={processing}
                      className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-bold inline-flex items-center gap-2"
                    >
                      <FiCreditCard />
                      {processing ? 'Processing...' : `Pay ₹${INTERNSHIP_PRICE_RUPEES.toLocaleString('en-IN')}`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

    </>
  );
}
